import { useEffect } from "react";
import { useLocation } from "react-router";

type PageMetadata = {
  title: string;
  description: string;
  canonicalPath: string;
  imagePath?: string;
  robots: "index,follow" | "noindex,nofollow";
};

const SITE_NAME = "Pragmatic Email Validator";
const DEFAULT_IMAGE_PATH = "/og-card.png";
const DEFAULT_THEME_COLOR = "#1565c0";

const DEFAULT_METADATA: PageMetadata = {
  title: SITE_NAME,
  description:
    "Validate email addresses using real-world provider rules, TLD checks, and pragmatic syntax validation.",
  canonicalPath: "/",
  imagePath: DEFAULT_IMAGE_PATH,
  robots: "index,follow",
};

const ROUTE_METADATA: Record<string, PageMetadata> = {
  "/": {
    title: `Validate Email Addresses | ${SITE_NAME}`,
    description:
      "Check whether an email address is valid with provider-aware rules, domain checks, and pragmatic formatting logic.",
    canonicalPath: "/",
    imagePath: DEFAULT_IMAGE_PATH,
    robots: "index,follow",
  },
  "/history": {
    title: `Validation History | ${SITE_NAME}`,
    description:
      "Review validation results captured during this session, including provider detection and status outcomes.",
    canonicalPath: "/history",
    imagePath: DEFAULT_IMAGE_PATH,
    robots: "noindex,nofollow",
  },
  "/compare": {
    title: `Compare Providers | ${SITE_NAME}`,
    description:
      "Compare email validation outcomes across all provider rule sets, with provider-by-provider results.",
    canonicalPath: "/compare",
    imagePath: DEFAULT_IMAGE_PATH,
    robots: "index,follow",
  },
  "/learn": {
    title: `Learn Practical Email Validation | ${SITE_NAME}`,
    description:
      "Explore how pragmatic email validation differs from RFC-only checks, including provider rules, IDNs, and reserved domains.",
    canonicalPath: "/learn",
    imagePath: DEFAULT_IMAGE_PATH,
    robots: "index,follow",
  },
  "/hammer": {
    title: `Hammer Test Tool | ${SITE_NAME}`,
    description:
      "Load test the validation API with concurrent requests and observe rate limiting behavior.",
    canonicalPath: "/hammer",
    imagePath: DEFAULT_IMAGE_PATH,
    robots: "noindex,nofollow",
  },
};

function getSiteBaseUrl() {
  const configured = import.meta.env.VITE_SITE_URL?.trim();
  if (configured && /^https?:\/\//i.test(configured)) {
    return configured.replace(/\/+$/, "");
  }
  return window.location.origin;
}

function toAbsoluteUrl(baseUrl: string, path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, `${baseUrl}/`).toString();
}

function ensureMetaTag(
  attribute: "name" | "property",
  value: string,
): HTMLMetaElement {
  const existing = document.querySelector(
    `meta[${attribute}="${value}"]`,
  ) as HTMLMetaElement | null;

  if (existing) {
    return existing;
  }

  const meta = document.createElement("meta");
  meta.setAttribute(attribute, value);
  document.head.appendChild(meta);
  return meta;
}

function setMetaContent(
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  const meta = ensureMetaTag(attribute, key);
  meta.setAttribute("content", content);
}

function ensureCanonicalLink() {
  const existing = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;

  if (existing) {
    return existing;
  }

  const link = document.createElement("link");
  link.setAttribute("rel", "canonical");
  document.head.appendChild(link);
  return link;
}

function normalizePathname(pathname: string) {
  if (!pathname || pathname === "/") {
    return "/";
  }
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function getMetadataForPath(pathname: string): PageMetadata {
  const normalizedPath = normalizePathname(pathname);
  if (ROUTE_METADATA[normalizedPath]) {
    return ROUTE_METADATA[normalizedPath];
  }

  return {
    ...DEFAULT_METADATA,
    canonicalPath: normalizedPath,
  };
}

export function usePageMetadata() {
  const location = useLocation();

  useEffect(() => {
    const metadata = getMetadataForPath(location.pathname);
    const baseUrl = getSiteBaseUrl();
    const canonicalUrl = toAbsoluteUrl(baseUrl, metadata.canonicalPath);
    const imageUrl = toAbsoluteUrl(
      baseUrl,
      metadata.imagePath ?? DEFAULT_IMAGE_PATH,
    );

    document.title = metadata.title;

    setMetaContent("name", "description", metadata.description);
    setMetaContent("name", "theme-color", DEFAULT_THEME_COLOR);
    setMetaContent("name", "robots", metadata.robots);

    setMetaContent("property", "og:type", "website");
    setMetaContent("property", "og:site_name", SITE_NAME);
    setMetaContent("property", "og:title", metadata.title);
    setMetaContent("property", "og:description", metadata.description);
    setMetaContent("property", "og:url", canonicalUrl);
    setMetaContent("property", "og:image", imageUrl);
    setMetaContent("property", "og:image:type", "image/png");
    setMetaContent("property", "og:image:width", "1200");
    setMetaContent("property", "og:image:height", "630");
    setMetaContent(
      "property",
      "og:image:alt",
      "Pragmatic Email Validator social preview image",
    );

    setMetaContent("name", "twitter:card", "summary_large_image");
    setMetaContent("name", "twitter:title", metadata.title);
    setMetaContent("name", "twitter:description", metadata.description);
    setMetaContent("name", "twitter:image", imageUrl);

    const canonicalLink = ensureCanonicalLink();
    canonicalLink.setAttribute("href", canonicalUrl);
  }, [location.pathname]);
}
